import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Maximize2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FinanceV2Section } from './FinanceV2Section';
import { formatValue } from '../../utils/numberFormat';

type ValueAtStakeRecord = Record<string, unknown>;

interface ValueAtStakeSectionProps {
  data?: ValueAtStakeRecord | null;
  title?: ReactNode;
  framed?: boolean;
}

const LOW_VALUE_KEYS = ['low_usd', 'min_usd', 'range_low_usd', 'usd_low', 'low'];
const HIGH_VALUE_KEYS = ['high_usd', 'max_usd', 'range_high_usd', 'usd_high', 'high', 'usd_at_stake', 'usd_value', 'value_usd', 'impact_usd'];
const TOTAL_LOW_KEYS = ['total_low_usd', 'total_min_usd', 'total_low', 'low_usd'];
const TOTAL_HIGH_KEYS = ['total_high_usd', 'total_max_usd', 'total_high', 'total_usd', 'high_usd'];
const NAME_KEYS = ['lever', 'title', 'name', 'label', 'point', 'summary', 'description'];
const DETAIL_KEYS = ['inputs_summary', 'rationale', 'detail', 'description', 'summary', 'point'];
const PREFERRED_META_KEYS = ['horizon', 'method'];
const VALUE_AT_STAKE_COMPACT_WIDTH = 760;

function isRecord(value: unknown): value is ValueAtStakeRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isPresent(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.some(isPresent);
  if (isRecord(value)) return Object.values(value).some(isPresent);
  return true;
}

function firstString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value !== 'string' && typeof value !== 'number') continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
}

function labelFromKey(value: unknown): string {
  return firstString(value)
    .replace(/^\$/, '$ ')
    .replace(/[_-]+/g, ' ')
    .replace(/\s*\/\s*/g, ' / ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\busd\b/gi, 'USD')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/[$,%\s,]/g, ''));
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function pickNumber(record: ValueAtStakeRecord, keys: string[]): { key: string; value: number } | null {
  for (const key of keys) {
    const numeric = toFiniteNumber(record[key]);
    if (numeric !== null) return { key, value: numeric };
  }
  return null;
}

function getItemName(item: ValueAtStakeRecord, index: number): { key: string; value: string } {
  for (const key of NAME_KEYS) {
    const value = firstString(item[key]);
    if (value) return { key, value };
  }
  return { key: 'item', value: `Item ${index + 1}` };
}

function getRange(record: ValueAtStakeRecord): { low: number | null; high: number | null; lowKey?: string; highKey?: string } {
  const low = pickNumber(record, LOW_VALUE_KEYS);
  const high = pickNumber(record, HIGH_VALUE_KEYS);
  return {
    low: low?.value ?? null,
    high: high?.value ?? low?.value ?? null,
    lowKey: low?.key,
    highKey: high?.key,
  };
}

function getTotalRange(data: ValueAtStakeRecord): { low: number | null; high: number | null } {
  const low = pickNumber(data, TOTAL_LOW_KEYS);
  const high = pickNumber(data, TOTAL_HIGH_KEYS);
  return {
    low: low?.value ?? null,
    high: high?.value ?? low?.value ?? null,
  };
}

function formatUsd(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '--';
  return formatValue(value, { unit: '$' });
}

function formatRange(low: number | null, high: number | null): string {
  if (low === null && high === null) return '--';
  if (low !== null && high !== null && low !== high) return `${formatUsd(low)}-${formatUsd(high)}`;
  return formatUsd(high ?? low);
}

function ValueChip({ value, tone }: { value: unknown; tone?: string }) {
  const text = firstString(value);
  if (!text) return <span className="text-[#A8989F]">--</span>;
  return (
    <span className={cn('inline-flex rounded-[6px] border px-2.5 py-1 font-display text-[11px] font-medium uppercase tracking-[0.08em]', tone || 'border-[#EDE6E1] bg-[#FAF7F1] text-[#3F3338]')}>
      {text.replace(/_/g, ' ')}
    </span>
  );
}

function useMeasuredWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const updateWidth = () => {
      setWidth(Math.round(element.getBoundingClientRect().width));
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      setWidth(Math.round(entry.contentRect.width));
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return { ref, width };
}

function HeroTotal({ low, high, label }: { low: number | null; high: number | null; label?: string }) {
  if (low === null && high === null) return null;
  return (
    <div className="flex flex-col items-center gap-2 py-4 text-center">
      {label && (
        <span className="font-display text-[12px] font-semibold uppercase tracking-[0.24em] text-[#8B7A82]">
          {label}
        </span>
      )}
      <div className="flex flex-wrap items-baseline justify-center gap-2 font-display text-[clamp(32px,5vw,52px)] font-light leading-none text-[#1F1A20]">
        <span>{formatUsd(low ?? high)}</span>
        {low !== null && high !== null && low !== high && (
          <>
            <span className="text-[0.55em] text-[#8B7A82]">-</span>
            <span>{formatUsd(high)}</span>
          </>
        )}
      </div>
    </div>
  );
}

function OpportunityBars({ items, compact = false }: { items: ValueAtStakeRecord[]; compact?: boolean }) {
  const rows = items.map((item, index) => {
    const name = getItemName(item, index).value;
    const range = getRange(item);
    return { name, ...range };
  }).filter((row) => row.low !== null || row.high !== null);

  if (rows.length === 0) return null;

  const maxValue = Math.max(...rows.map((row) => Math.abs(row.high ?? row.low ?? 0)), 1);
  const tickPercentages = compact ? [0, 0.5, 1] : [0, 0.25, 0.5, 0.75, 1];
  const ticks = tickPercentages.map((pct) => maxValue * pct);

  return (
    <div className="mt-4">
      <div className="grid gap-3">
        {rows.map((row) => {
          const width = Math.max(3, Math.min(100, (Math.abs(row.high ?? row.low ?? 0) / maxValue) * 100));
          const rangeLabel = formatRange(row.low, row.high);
          const keepLabelInside = width > 78;
          return (
            <div
              key={`${row.name}-${row.high}`}
              className={cn(
                'grid min-w-0 items-center gap-3',
                !compact && 'md:grid-cols-[minmax(170px,220px)_minmax(0,1fr)]'
              )}
            >
              {compact ? (
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0 font-display text-[13px] leading-snug text-[#2B252A]">
                    {row.name}
                  </div>
                  <div className="shrink-0 whitespace-nowrap font-mono text-[12px] text-[#2B252A]" title={rangeLabel}>
                    {rangeLabel}
                  </div>
                </div>
              ) : (
                <div className="font-display text-[13px] leading-snug text-[#2B252A] md:text-right">
                  {row.name}
                </div>
              )}
              <div className="min-w-0">
                <div className="relative h-8 overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 right-0 grid"
                    style={{ gridTemplateColumns: `repeat(${tickPercentages.length - 1}, minmax(0, 1fr))` }}
                  >
                    {tickPercentages.slice(0, -1).map((tick) => (
                      <div key={tick} className="border-l border-[#EDE5D8] last:border-r" />
                    ))}
                  </div>
                  <div className="absolute left-0 top-1/2 h-[14px] -translate-y-1/2 rounded-[3px] bg-[#E8B84F]" style={{ width: `${width}%` }} />
                  {!compact && (
                    <div
                      className={cn(
                        'absolute top-1/2 -translate-y-1/2 overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[11.5px] text-[#2B252A]',
                        keepLabelInside
                          ? 'right-1 max-w-[48%] rounded bg-white/70 px-1 text-right'
                          : 'pl-2'
                      )}
                      style={keepLabelInside ? undefined : { left: `${width}%`, maxWidth: `calc(${100 - width}% - 8px)` }}
                      title={rangeLabel}
                    >
                      {rangeLabel}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className={cn('mt-2 grid gap-3', !compact && 'md:grid-cols-[minmax(170px,220px)_minmax(0,1fr)]')}>
        {!compact && <div />}
        <div
          className="grid font-mono text-[11px] text-[#7B6B72]"
          style={{ gridTemplateColumns: `repeat(${ticks.length}, minmax(0, 1fr))` }}
        >
          {ticks.map((tick) => (
            <span key={tick} className="first:text-left text-center last:text-right">{formatUsd(tick)}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function ValueAtStakeContent({
  total,
  label,
  items,
  rawItems,
  compactBars,
}: {
  total: { low: number | null; high: number | null };
  label?: string;
  items: ValueAtStakeRecord[];
  rawItems: unknown[];
  compactBars: boolean;
}) {
  return (
    <div className="overflow-hidden rounded-[14px] border border-[#EDE6E1] bg-white px-4 py-5 shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)] md:px-6">
      <HeroTotal low={total.low} high={total.high} label={label ? labelFromKey(label) : undefined} />
      <OpportunityBars items={items} compact={compactBars} />
      <StructuredTable items={items} />
      <ExplicitBullets items={rawItems} />
    </div>
  );
}

function ValueAtStakeExpandedModal({
  open,
  onClose,
  title,
  total,
  label,
  items,
  rawItems,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  total: { low: number | null; high: number | null };
  label?: string;
  items: ValueAtStakeRecord[];
  rawItems: unknown[];
}) {
  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#1F1A20]/45 p-4 backdrop-blur-sm"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label="Expanded dollars at stake"
        className="flex h-[min(90vh,820px)] w-[min(94vw,1180px)] flex-col overflow-hidden rounded-[14px] border border-[#EDE6E1] bg-[#FAF7F1] text-[#1F1A20] shadow-[0_24px_70px_-28px_rgba(31,26,32,.55)]"
      >
        <header className="flex shrink-0 items-center justify-between gap-4 border-b border-[#EDE5D8] bg-white px-5 py-4">
          <div className="min-w-0">
            <div className="font-display text-[13px] font-semibold uppercase tracking-[0.24em] text-[#7B6B72]">
              {title || 'Dollars at stake'}
            </div>
            <div className="mt-1 text-[12px] italic text-[#7B6B72]">
              Expanded view for the full opportunity chart and supporting table.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] border border-[#EDE6E1] bg-white text-[#7B6B72] transition-colors hover:border-[#6E3450]/35 hover:bg-[#FAF7F1] hover:text-[#6E3450]"
            aria-label="Close expanded dollars at stake"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-auto p-5">
          <ValueAtStakeContent
            total={total}
            label={label}
            items={items}
            rawItems={rawItems}
            compactBars={false}
          />
        </div>
      </section>
    </div>
  );
}

function getDetailLines(item: ValueAtStakeRecord, primaryText: string): string[] {
  return DETAIL_KEYS
    .map((key) => firstString(item[key]))
    .filter((value, index, values) => value && value !== primaryText && values.indexOf(value) === index);
}

function StructuredTable({ items }: { items: ValueAtStakeRecord[] }) {
  if (items.length === 0) return null;

  const firstNameKey = items.map((item, index) => getItemName(item, index).key).find(Boolean) || 'item';
  const hasRange = items.some((item) => {
    const range = getRange(item);
    return range.low !== null || range.high !== null;
  });
  const firstRange = items.map(getRange).find((range) => range.lowKey || range.highKey);
  const rangeLabelKey = [firstRange?.lowKey, firstRange?.highKey].filter(Boolean).join(' / ');
  const availableMetaKeys = PREFERRED_META_KEYS.filter((key) => items.some((item) => isPresent(item[key])));
  const consumedKeys = new Set([...LOW_VALUE_KEYS, ...HIGH_VALUE_KEYS, ...NAME_KEYS, ...DETAIL_KEYS, 'id', 'visual']);
  const extraKeys = Array.from(new Set(items.flatMap((item) => Object.keys(item))))
    .filter((key) => !consumedKeys.has(key) && !availableMetaKeys.includes(key))
    .filter((key) => items.some((item) => isPresent(item[key]) && !isRecord(item[key]) && !Array.isArray(item[key])))
    .slice(0, Math.max(0, 5 - availableMetaKeys.length));

  return (
    <div className="mt-6 overflow-x-auto">
      <table className="w-full min-w-[680px] text-[13px]">
        <thead>
          <tr className="border-b border-[#EDE5D8]">
            <th className="py-2 pr-4 text-left font-display text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8B7A82]">
              {labelFromKey(firstNameKey)}
            </th>
            {hasRange && (
              <th className="py-2 pr-4 text-left font-display text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8B7A82]">
                {labelFromKey(rangeLabelKey)}
              </th>
            )}
            {[...availableMetaKeys, ...extraKeys].map((key) => (
              <th key={key} className="py-2 pr-4 text-left font-display text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8B7A82]">
                {labelFromKey(key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const name = getItemName(item, index);
            const range = getRange(item);
            const detailLines = getDetailLines(item, name.value);
            return (
              <tr key={firstString(item.id, `${name.value}-${index}`)} className="border-b border-[#EFE6E1] align-top last:border-0">
                <td className="py-3 pr-4">
                  <div className="font-display text-[14px] leading-snug text-[#1F1A20]">{name.value}</div>
                  {detailLines.map((line) => (
                    <div key={line} className="mt-1 text-[11.5px] italic leading-snug text-[#7B6B72]">{line}</div>
                  ))}
                </td>
                {hasRange && (
                  <td className="whitespace-nowrap py-3 pr-4 font-mono text-[12px] text-[#1F1A20]">
                    {formatRange(range.low, range.high)}
                  </td>
                )}
                {[...availableMetaKeys, ...extraKeys].map((key) => (
                  <td key={key} className="py-3 pr-4 text-[12.5px] text-[#7B6B72]">
                    {key === 'method' ? (
                      <ValueChip value={item[key]} />
                    ) : (
                      firstString(item[key]) || '--'
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ExplicitBullets({ items }: { items: unknown[] }) {
  const bullets = items.filter((item) => !isRecord(item)).map(firstString).filter(Boolean);
  if (bullets.length === 0) return null;

  return (
    <ul className="mt-4 list-disc space-y-2 pl-5 font-display text-[14px] leading-7 text-[#3F3338]">
      {bullets.map((item) => <li key={item}>{item}</li>)}
    </ul>
  );
}

export function ValueAtStakeSection({ data, title, framed = false }: ValueAtStakeSectionProps) {
  const { ref, width } = useMeasuredWidth<HTMLDivElement>();
  const [expanded, setExpanded] = useState(false);

  if (!data) return null;

  const rawItems = Array.isArray(data.items) ? data.items : [];
  const items = rawItems.filter(isRecord);
  const total = getTotalRange(data);
  const hasTotal = total.low !== null || total.high !== null;
  const hasStructuredItems = items.length > 0;
  const label = firstString(data.total_label, data.totalTitle, data.metric_label, data.summary_label, data.opportunity_label);

  if (!hasTotal && !hasStructuredItems && rawItems.length === 0) return null;

  const compactBars = width !== null && width < VALUE_AT_STAKE_COMPACT_WIDTH;
  // V2-only: show expand only when the measured chart area is cramped enough for labels to collide.
  const canExpand = compactBars && hasStructuredItems;
  const content = (
    <div ref={ref} className="min-w-0">
      {canExpand && (
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex h-8 items-center gap-1.5 rounded-[8px] bg-[#1F1A20] px-3 text-[12px] font-medium text-white transition-colors hover:bg-[#3F3338]"
            aria-label="Expand dollars at stake"
          >
            <Maximize2 className="h-3.5 w-3.5" />
            Expand
          </button>
        </div>
      )}
      <ValueAtStakeContent
        total={total}
        label={label}
        items={items}
        rawItems={rawItems}
        compactBars={compactBars}
      />
      <ValueAtStakeExpandedModal
        open={expanded}
        onClose={() => setExpanded(false)}
        title={title || 'Dollars at stake'}
        total={total}
        label={label}
        items={items}
        rawItems={rawItems}
      />
    </div>
  );

  if (framed) {
    return <FinanceV2Section title={title}>{content}</FinanceV2Section>;
  }

  return content;
}
